import keycloak from "@/config/keycloak";
import { createContext, useContext, useEffect, useState, useRef } from "react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";
import { UserRole } from "@/types/enums";

interface KeycloakContextType {
  keycloak: typeof keycloak;
  authenticated: boolean;
  loading: boolean;
  login: () => void;
  logout: () => void;
  token: string | undefined;
}

const KeycloakContext = createContext<KeycloakContextType | undefined>(undefined);

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within KeycloakProvider');
  }
  return context;
};

export const KeycloakProvider = ({ children }: { children: React.ReactNode }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);
  
  const loginUser = (profile?: { id: string; email: string; role: UserRole }) => {
    if (!keycloak.token) return;
    
    const userData = profile?.id
      ? {
          id: profile.id,
          email: profile.email,
          name: profile.email, // Keycloak не надає name, використовуємо email
          role: profile.role,
        }
      : {
          id: '',
          email: keycloak.tokenParsed?.email || keycloak.tokenParsed?.preferred_username || '',
          name: keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username || '',
          role: UserRole.USER,
        };
    
    useAuthStore.getState().login({
      access_token: keycloak.token,
      user: userData,
    });
  };
  
  // Функція для обробки успішної авторизації
  const handleSuccessfulAuth = async (hasCode: boolean = false) => {
    if (!keycloak.token) return;
    
    localStorage.setItem("accessToken", keycloak.token);
    
    // Завантажуємо профіль користувача з бекенду та заповнюємо authStore
    try {
      const profile = await apiService.getProfile();
      // Бекенд повертає об'єкт профілю напряму, не обгорнутий в { user: ... }
      loginUser(profile);
    } catch (error) {
      // Якщо не вдалося завантажити профіль, встановлюємо базові дані з токена
      loginUser();
    }
    
    // Очищаємо URL від параметрів після успішної авторизації
    if (hasCode) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Перевірка чи є code в URL (після редиректу з Keycloak)
  const hasCodeInUrl = (): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    return urlParams.has('code') || hashParams.has('code') || window.location.hash.includes('code');
  };

  // Визначення режиму завантаження Keycloak
  const getOnLoadMode = (storedToken: string | null, hasCode: boolean): "check-sso" | "login-required" => {
    if (storedToken && !hasCode) return "check-sso";
    if (hasCode) return "login-required";
    return "check-sso";
  };

  // Перевірка подвійної ініціалізації (React StrictMode)
  const checkDoubleInit = (storedToken: string | null): boolean => {
    if (!initRef.current) return false;

    // Якщо є токен в localStorage, але Keycloak ще не перевірив - залишаємо loading = true
    if (storedToken && !keycloak.authenticated) {
      return true; // Чекаємо поки Keycloak перевірить токен
    }

    if (keycloak.authenticated && keycloak.token) {
      setAuthenticated(true);
      setLoading(false);
    } else {
      // Якщо немає токена або Keycloak не авторизований - точно не авторизований
      if (storedToken) {
        localStorage.removeItem("accessToken");
      }
      setLoading(false);
    }
    return true;
  };

  // Налаштування обробників подій Keycloak
  const setupKeycloakEventHandlers = () => {
    // Обробка події успішної авторизації (після редиректу з Keycloak)
    keycloak.onAuthSuccess = async () => {
      setAuthenticated(true);
      if (keycloak.token) {
        await handleSuccessfulAuth(true);
      }
      setLoading(false);
    };

    // Обробка помилок авторизації
    keycloak.onAuthError = () => {
      setAuthenticated(false);
      localStorage.removeItem("accessToken");
      useAuthStore.getState().logout();
      setLoading(false);
    };

    // Обробка виходу
    keycloak.onAuthLogout = () => {
      setAuthenticated(false);
      localStorage.removeItem("accessToken");
      useAuthStore.getState().logout();
      setLoading(false);
    };
  };

  // Очищення обробників подій Keycloak
  const cleanupKeycloakEventHandlers = () => {
    keycloak.onAuthSuccess = undefined;
    keycloak.onAuthError = undefined;
    keycloak.onAuthLogout = undefined;
  };

  // Ініціалізація Keycloak
  const initializeKeycloak = async (storedToken: string | null, hasCode: boolean) => {
    const onLoadMode = getOnLoadMode(storedToken, hasCode);

    try {
      const auth = await keycloak.init({
        onLoad: onLoadMode,
        pkceMethod: "S256",
        checkLoginIframe: true,
      });

      setAuthenticated(auth);
      if (auth && keycloak.token) {
        await handleSuccessfulAuth(hasCode);
      } else {
        // Якщо перевірка не пройшла, але є токен в localStorage - видаляємо його
        if (storedToken) {
          localStorage.removeItem("accessToken");
        }
      }
    } catch (error: any) {
      // Ігноруємо помилку "can only be initialized once"
      if (error.message?.includes('can only be initialized once')) {
        if (keycloak.authenticated && keycloak.token) {
          setAuthenticated(true);
          // Завантажуємо профіль якщо ще не завантажений
          if (!useAuthStore.getState().user) {
            await handleSuccessfulAuth();
          }
        }
        return;
      }
      setAuthenticated(false);
    } finally { 
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const storedToken = localStorage.getItem("accessToken");
    
    // Запобігаємо подвійній ініціалізації (React StrictMode)
    if (checkDoubleInit(storedToken)) {
      return;
    }
    
    initRef.current = true;
    const hasCode = hasCodeInUrl();
    
    // Налаштування обробників подій
    setupKeycloakEventHandlers();
    
    // Ініціалізація Keycloak
    initializeKeycloak(storedToken, hasCode);

    // Cleanup
    return cleanupKeycloakEventHandlers;
  }, []);
  
  const handleLogin = () => {
    keycloak.login();
  };
  
  const handleLogout = () => {
    keycloak.logout();
  };
  
  return (
    <KeycloakContext.Provider
      value={{
        keycloak,
        authenticated,
        login: handleLogin,
        logout: handleLogout,
        token: keycloak.token,
        loading,
      }}
    >
      {children}
    </KeycloakContext.Provider>
  );
};